import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ChatHeader from './ChatHeader';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { Message } from '../utils/chatUtils';
import { FormField } from '../utils/chatUtils';

interface ChatWindowProps {
  isOpen: boolean;
  isMobile: boolean;
  botConfig: {
    botName: string;
    botSubtitle: string;
  };
  messages: Message[];
  currentMessage: string;
  isTyping: boolean;
  isContactFormMode: boolean;
  contactFormStep: FormField | 'confirmation' | null;
  isSubmitting: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  showSettings: boolean;
  toggleChat: () => void;
  startContactForm: () => void;
  exitContactForm: () => void;
  setCurrentMessage: (message: string) => void;
  handleSendMessage: () => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  isOpen,
  isMobile,
  botConfig,
  messages,
  currentMessage,
  isTyping,
  isContactFormMode,
  contactFormStep,
  isSubmitting,
  messagesEndRef,
  showSettings,
  toggleChat,
  startContactForm,
  exitContactForm,
  setCurrentMessage,
  handleSendMessage,
  handleKeyPress
}) => {
  const chatWindowRef = useRef<HTMLDivElement>(null);
  
  // Default dimensions (20% larger)
  const defaultWidth = 456; // 380px * 1.2 = 456px
  const defaultHeight = 600; // 500px * 1.2 = 600px
  
  // Force a layout recalculation when the chat window opens
  useEffect(() => {
    if (isOpen && chatWindowRef.current) {
      // Small timeout to ensure DOM is ready
      const timer = setTimeout(() => {
        // Force a reflow
        if (chatWindowRef.current) {
          const height = chatWindowRef.current.offsetHeight;
          chatWindowRef.current.style.height = `${height}px`;
          
          // Force scroll to bottom
          const messagesContainer = chatWindowRef.current.querySelector('.chat-messages');
          if (messagesContainer instanceof HTMLElement) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
          }
        }
        
        window.dispatchEvent(new Event('resize'));
      }, 50);
      
      // Set up multiple scroll attempts with increasing delays
      const scrollTimers = [200, 500, 1000].map(delay => 
        setTimeout(() => {
          const messagesContainer = chatWindowRef.current?.querySelector('.chat-messages');
          if (messagesContainer instanceof HTMLElement) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
          }
        }, delay)
      );
      
      return () => {
        clearTimeout(timer);
        scrollTimers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [isOpen]);
  
  // Handle animation completion
  const handleAnimationComplete = () => {
    // Force scroll to bottom after animation completes
    if (chatWindowRef.current) {
      const messagesContainer = chatWindowRef.current.querySelector('.chat-messages');
      if (messagesContainer instanceof HTMLElement) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }
  };

  return (
    <motion.div
      ref={chatWindowRef}
      className="fixed bottom-6 right-6 z-50 flex flex-col chat-window"
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ 
        opacity: 1, 
        scale: 1, 
        y: 0,
        bottom: isMobile ? 0 : '1.5rem',
        right: isMobile ? 0 : '1.5rem',
      }}
      exit={{ opacity: 0, scale: 0.9, y: 20 }}
      transition={{ duration: 0.2 }}
      onAnimationComplete={handleAnimationComplete}
      style={{
        width: isMobile ? "100%" : `${defaultWidth}px`,
        height: isMobile ? "100%" : `${defaultHeight}px`,
        maxHeight: isMobile ? "100%" : "calc(100vh - 80px)",
        display: "flex",
        flexDirection: "column",
        boxShadow: isMobile ? "none" : "0 8px 16px rgba(0, 0, 0, 0.1)",
        padding: isMobile ? "0" : "0",
        paddingBottom: isMobile ? "env(safe-area-inset-bottom, 0px)" : "0",
        paddingTop: isMobile ? "env(safe-area-inset-top, 0px)" : "0",
        backgroundColor: "var(--chat-bg)",
        borderRadius: isMobile ? "0" : "12px",
        zIndex: 9999,
        overflow: "hidden",
      }}
    >
      <ChatHeader 
        botName={botConfig.botName}
        botSubtitle={botConfig.botSubtitle}
        showSettings={showSettings}
        onStartContactForm={startContactForm}
        onToggleChat={toggleChat}
        isContactFormMode={isContactFormMode}
      />
      
      <ChatMessages 
        messages={messages}
        isTyping={isTyping}
        messagesEndRef={messagesEndRef}
      />
      
      <ChatInput 
        currentMessage={currentMessage}
        setCurrentMessage={setCurrentMessage}
        handleSendMessage={handleSendMessage}
        handleKeyPress={handleKeyPress}
        isContactFormMode={isContactFormMode}
        contactFormStep={contactFormStep}
        exitContactForm={exitContactForm}
        isSubmitting={isSubmitting}
      />
    </motion.div>
  );
};

export default ChatWindow;
